import { C } from "../constants/colors";

const ff = "'Outfit',sans-serif";
const label = (x, y, text, color, size=11, anchor="middle", weight="400") => (
  <text x={x} y={y} fontSize={size} fill={color||C.textMid} fontFamily={ff} textAnchor={anchor} fontWeight={weight}>{text}</text>
);

// ═══════════════════════════════════════
//  SOUND (Class 9 Ch 11)
// ═══════════════════════════════════════

export function SvgSoundWave() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Sound — longitudinal wave", C.text, 13, "middle", "600")}
      {/* Compressions and rarefactions */}
      {[0,1,2,3].map(i => {
        const x = 60 + i * 90;
        return (
          <g key={i}>
            {/* Compression — dense lines */}
            {[-8,-4,0,4,8].map(d => <line key={`c${i}${d}`} x1={x+d} y1="50" x2={x+d} y2="130" stroke={C.primaryMid} strokeWidth="1.5" opacity={1 - Math.abs(d)/14}/>)}
            {/* Rarefaction — sparse lines */}
            {[-15,-8,0,8,15].map(d => <line key={`r${i}${d}`} x1={x+45+d} y1="50" x2={x+45+d} y2="130" stroke={C.primaryLight} strokeWidth="1" opacity="0.5"/>)}
          </g>
        );
      })}
      {/* Wave representation below */}
      <path d="M 40 160 Q 70 140 100 160 Q 130 180 160 160 Q 190 140 220 160 Q 250 180 280 160 Q 310 140 340 160 Q 370 180 380 160" fill="none" stroke={C.primaryMid} strokeWidth="2"/>
      {/* Labels */}
      <line x1="60" y1="145" x2="60" y2="155" stroke={C.amberDark} strokeWidth="1"/>
      <line x1="150" y1="145" x2="150" y2="155" stroke={C.amberDark} strokeWidth="1"/>
      <line x1="240" y1="145" x2="240" y2="155" stroke={C.amberDark} strokeWidth="1"/>
      {label(105, 145, "λ (wavelength)", C.amberDark, 10, "middle", "500")}
      {/* C and R labels */}
      {label(60, 42, "C", C.primaryMid, 10, "middle", "600")}
      {label(105, 42, "R", C.textMute, 10, "middle", "600")}
      {label(150, 42, "C", C.primaryMid, 10, "middle", "600")}
      {label(195, 42, "R", C.textMute, 10, "middle", "600")}
      {label(200, 192, "C = compression (high pressure) | R = rarefaction (low pressure)", C.textMute, 9)}
    </svg>
  );
}

export function SvgSonarEcho() {
  return (
    <svg viewBox="0 0 400 180" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Echo and SONAR", C.text, 13, "middle", "600")}
      {/* Source */}
      <rect x="30" y="60" width="40" height="60" rx="4" fill={C.primarySoft} stroke={C.primaryMid} strokeWidth="1.5"/>
      {label(50, 95, "Source", C.primaryMid, 9, "middle", "500")}
      {/* Wall */}
      <rect x="340" y="40" width="15" height="100" rx="2" fill="#d1d5db" stroke="#9ca3af" strokeWidth="1.5"/>
      {label(347, 155, "Wall", C.textMute, 9, "middle")}
      {/* Outgoing wave */}
      <line x1="70" y1="80" x2="340" y2="80" stroke={C.amberDark} strokeWidth="2" strokeDasharray="8,4"/>
      <polygon points="340,80 330,75 330,85" fill={C.amberDark}/>
      {label(200, 72, "Sound wave →", C.amberDark, 10, "middle", "500")}
      {/* Reflected wave */}
      <line x1="340" y1="100" x2="70" y2="100" stroke="#E24B4A" strokeWidth="2" strokeDasharray="8,4"/>
      <polygon points="70,100 80,95 80,105" fill="#E24B4A"/>
      {label(200, 118, "← Echo (reflected sound)", "#E24B4A", 10, "middle", "500")}
      {/* Distance */}
      <line x1="50" y1="140" x2="347" y2="140" stroke={C.textMid} strokeWidth="1"/>
      {label(200, 136, "d", C.textMid, 11, "middle", "500")}
      {label(200, 168, "d = v × t ÷ 2 (min 17.2m for echo)", C.text, 11, "middle", "500")}
    </svg>
  );
}

// ═══════════════════════════════════════
//  MAGNETIC EFFECTS (Class 10 Ch 12)
// ═══════════════════════════════════════

export function SvgMagneticFieldLines() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Magnetic field lines — bar magnet", C.text, 13, "middle", "600")}
      {/* Bar magnet */}
      <rect x="140" y="85" width="60" height="30" rx="3" fill="#E24B4A" stroke="#A32D2D" strokeWidth="1.5"/>
      <rect x="200" y="85" width="60" height="30" rx="3" fill="#3b82f6" stroke="#185FA5" strokeWidth="1.5"/>
      {label(170, 104, "N", "white", 14, "middle", "600")}
      {label(230, 104, "S", "white", 14, "middle", "600")}
      {/* Field lines */}
      <path d="M 140 100 Q 100 100 80 70 Q 60 30 200 30 Q 340 30 320 70 Q 300 100 260 100" fill="none" stroke={C.primaryMid} strokeWidth="1.5"/>
      <path d="M 140 100 Q 110 100 95 80 Q 75 50 200 50 Q 325 50 305 80 Q 290 100 260 100" fill="none" stroke={C.primaryMid} strokeWidth="1.5"/>
      <path d="M 140 100 Q 110 100 95 120 Q 75 150 200 150 Q 325 150 305 120 Q 290 100 260 100" fill="none" stroke={C.primaryMid} strokeWidth="1.5"/>
      <path d="M 140 100 Q 100 100 80 130 Q 60 170 200 170 Q 340 170 320 130 Q 300 100 260 100" fill="none" stroke={C.primaryMid} strokeWidth="1.5"/>
      {/* Arrows on field lines */}
      <polygon points="80,70 85,64 75,64" fill={C.primaryMid}/>
      <polygon points="320,70 325,64 315,64" fill={C.primaryMid}/>
      <polygon points="80,130 85,136 75,136" fill={C.primaryMid}/>
      <polygon points="320,130 325,136 315,136" fill={C.primaryMid}/>
      {label(200, 195, "Lines go from N to S outside the magnet", C.textMute, 10)}
    </svg>
  );
}

export function SvgSolenoid() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Solenoid — coil acting as magnet", C.text, 13, "middle", "600")}
      {/* Coil turns */}
      {[0,1,2,3,4,5,6].map(i => (
        <ellipse key={i} cx={120+i*25} cy="100" rx="8" ry="35" fill="none" stroke={C.amberDark} strokeWidth="2"/>
      ))}
      {/* Current direction arrows */}
      <line x1="100" y1="135" x2="100" y2="160" stroke={C.amberDark} strokeWidth="1.5"/>
      <line x1="100" y1="160" x2="290" y2="160" stroke={C.amberDark} strokeWidth="1.5"/>
      {label(195, 175, "Current →", C.amberDark, 10, "middle", "500")}
      {/* Field lines inside */}
      <line x1="110" y1="100" x2="280" y2="100" stroke={C.primaryMid} strokeWidth="1.5"/>
      <polygon points="280,100 272,95 272,105" fill={C.primaryMid}/>
      {label(195, 92, "Uniform field inside", C.primaryMid, 9, "middle", "500")}
      {/* N and S labels */}
      <rect x="90" y="82" width="24" height="16" rx="3" fill="#E24B4A"/>
      {label(102, 93, "S", "white", 10, "middle", "600")}
      <rect x="284" y="82" width="24" height="16" rx="3" fill="#3b82f6"/>
      {label(296, 93, "N", "white", 10, "middle", "600")}
      {label(200, 195, "Solenoid behaves like a bar magnet when current flows", C.textMute, 10)}
    </svg>
  );
}

export function SvgElectricMotor() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Electric motor — principle", C.text, 13, "middle", "600")}
      {/* Magnet poles */}
      <rect x="50" y="60" width="30" height="80" rx="3" fill="#E24B4A" stroke="#A32D2D" strokeWidth="1.5"/>
      {label(65, 104, "N", "white", 12, "middle", "600")}
      <rect x="320" y="60" width="30" height="80" rx="3" fill="#3b82f6" stroke="#185FA5" strokeWidth="1.5"/>
      {label(335, 104, "S", "white", 12, "middle", "600")}
      {/* Coil (ABCD) */}
      <rect x="120" y="65" width="160" height="70" rx="4" fill="none" stroke={C.amberDark} strokeWidth="2.5"/>
      {label(120, 58, "A", C.text, 10, "middle", "600")}
      {label(280, 58, "B", C.text, 10, "middle", "600")}
      {label(280, 148, "C", C.text, 10, "middle", "600")}
      {label(120, 148, "D", C.text, 10, "middle", "600")}
      {/* Rotation arrow */}
      <path d="M 200 70 A 25 25 0 0 1 220 80" fill="none" stroke={C.primaryMid} strokeWidth="2"/>
      <polygon points="220,80 218,72 226,78" fill={C.primaryMid}/>
      {label(210, 62, "rotation", C.primaryMid, 9, "middle", "500")}
      {/* Axis */}
      <line x1="200" y1="55" x2="200" y2="145" stroke={C.textMute} strokeWidth="1" strokeDasharray="4,3"/>
      {/* Battery */}
      <line x1="150" y1="135" x2="150" y2="170" stroke={C.textMid} strokeWidth="1.5"/>
      <line x1="250" y1="135" x2="250" y2="170" stroke={C.textMid} strokeWidth="1.5"/>
      <line x1="150" y1="170" x2="250" y2="170" stroke={C.textMid} strokeWidth="1.5"/>
      {label(200, 182, "Battery", C.textMute, 9)}
      {label(200, 198, "Current-carrying coil in magnetic field → force → rotation", C.textMute, 10)}
    </svg>
  );
}

export function SvgEMInduction() {
  return (
    <svg viewBox="0 0 400 180" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Electromagnetic induction", C.text, 13, "middle", "600")}
      {/* Coil */}
      {[0,1,2,3,4].map(i => <ellipse key={i} cx={200+i*20} cy="90" rx="8" ry="40" fill="none" stroke={C.amberDark} strokeWidth="2"/>)}
      {/* Galvanometer */}
      <circle cx="300" cy="140" r="16" fill="none" stroke={C.primaryMid} strokeWidth="1.5"/>
      {label(300, 144, "G", C.primaryMid, 10, "middle", "600")}
      <line x1="200" y1="130" x2="200" y2="140" stroke={C.textMid} strokeWidth="1.5"/>
      <line x1="200" y1="140" x2="284" y2="140" stroke={C.textMid} strokeWidth="1.5"/>
      <line x1="280" y1="130" x2="280" y2="140" stroke={C.textMid} strokeWidth="1.5"/>
      <line x1="316" y1="140" x2="340" y2="140" stroke={C.textMid} strokeWidth="1.5"/>
      <line x1="340" y1="140" x2="340" y2="90" stroke={C.textMid} strokeWidth="1.5"/>
      <line x1="340" y1="90" x2="280" y2="90" stroke={C.textMid} strokeWidth="1.5"/>
      {/* Magnet moving */}
      <rect x="100" y="78" width="60" height="24" rx="4" fill="#EEEDFE" stroke={C.purpleMid} strokeWidth="1.5"/>
      {label(120, 94, "N", C.purpleMid, 10, "middle", "600")}
      {label(145, 94, "S", C.purpleMid, 10, "middle", "600")}
      <polygon points="170,90 180,85 180,95" fill={C.purpleMid}/>
      {label(175, 74, "push in →", C.purpleMid, 9, "middle", "500")}
      {label(200, 170, "Moving magnet in coil → changing flux → induced EMF", C.textMute, 10)}
    </svg>
  );
}

// ═══════════════════════════════════════
//  ATOMIC STRUCTURE (Class 9 Ch 4)
// ═══════════════════════════════════════

export function SvgBohrModel() {
  return (
    <svg viewBox="0 0 400 220" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Bohr's model — sodium (Na, Z=11)", C.text, 13, "middle", "600")}
      {/* Nucleus */}
      <circle cx="200" cy="110" r="18" fill="#FAEEDA" stroke={C.amberDark} strokeWidth="2"/>
      {label(200, 114, "11p", C.amberDark, 9, "middle", "600")}
      {label(200, 124, "12n", C.amberDark, 8, "middle")}
      {/* Shell K (2 electrons) */}
      <circle cx="200" cy="110" r="40" fill="none" stroke={C.primaryLight} strokeWidth="1.5"/>
      <circle cx="200" cy="70" r="5" fill={C.primaryMid}/>
      <circle cx="200" cy="150" r="5" fill={C.primaryMid}/>
      {label(248, 75, "K (2)", C.primaryMid, 9, "start", "500")}
      {/* Shell L (8 electrons) */}
      <circle cx="200" cy="110" r="65" fill="none" stroke={C.primaryLight} strokeWidth="1.5"/>
      {[0,45,90,135,180,225,270,315].map((a,i) => {
        const rad = (a * Math.PI) / 180;
        return <circle key={i} cx={200+65*Math.cos(rad)} cy={110+65*Math.sin(rad)} r="4" fill={C.purpleMid}/>;
      })}
      {label(275, 55, "L (8)", C.purpleMid, 9, "start", "500")}
      {/* Shell M (1 electron) */}
      <circle cx="200" cy="110" r="90" fill="none" stroke={C.primaryLight} strokeWidth="1.5"/>
      <circle cx="290" cy="110" r="5" fill="#E24B4A"/>
      {label(298, 30, "M (1)", "#E24B4A", 9, "start", "500")}
      {label(200, 210, "Electronic configuration: 2, 8, 1 → Valency = 1", C.text, 11, "middle", "500")}
    </svg>
  );
}

// ═══════════════════════════════════════
//  CELL STRUCTURE (Class 9 Ch 5)
// ═══════════════════════════════════════

export function SvgCellStructure() {
  return (
    <svg viewBox="0 0 400 240" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 16, "Animal cell vs Plant cell", C.text, 13, "middle", "600")}
      {/* Animal cell */}
      <ellipse cx="120" cy="130" rx="85" ry="75" fill="#FAEEDA" stroke={C.amberDark} strokeWidth="2"/>
      {label(120, 40, "Animal cell", C.amberDark, 11, "middle", "600")}
      {/* Nucleus */}
      <ellipse cx="120" cy="120" rx="25" ry="20" fill="#EEEDFE" stroke={C.purpleMid} strokeWidth="1.5"/>
      {label(120, 124, "Nucleus", C.purpleMid, 8, "middle", "500")}
      {/* Cell membrane */}
      {label(120, 210, "Cell membrane", C.amberDark, 8, "middle")}
      {/* Mitochondria */}
      <ellipse cx="80" cy="160" rx="12" ry="6" fill="#E1F5EE" stroke={C.primaryMid} strokeWidth="1"/>
      {label(80, 175, "Mito", C.primaryMid, 7)}
      {/* Cytoplasm label */}
      {label(148, 90, "Cytoplasm", C.textMute, 7, "start")}

      {/* Plant cell */}
      <rect x="240" y="55" width="140" height="150" rx="12" fill="#E1F5EE" stroke={C.primaryMid} strokeWidth="2.5"/>
      {label(310, 40, "Plant cell", C.primaryMid, 11, "middle", "600")}
      {/* Inner membrane */}
      <rect x="248" y="62" width="124" height="136" rx="10" fill="#FAEEDA" stroke={C.amberDark} strokeWidth="1" strokeDasharray="4,3"/>
      {/* Nucleus */}
      <ellipse cx="310" cy="120" rx="22" ry="18" fill="#EEEDFE" stroke={C.purpleMid} strokeWidth="1.5"/>
      {label(310, 124, "Nucleus", C.purpleMid, 8, "middle", "500")}
      {/* Vacuole */}
      <ellipse cx="300" cy="160" rx="28" ry="16" fill="#E6F1FB" stroke="#3b82f6" strokeWidth="1"/>
      {label(300, 164, "Vacuole", "#185FA5", 7, "middle", "500")}
      {/* Chloroplast */}
      <ellipse cx="270" cy="90" rx="10" ry="6" fill="#97C459" stroke="#3B6D11" strokeWidth="1"/>
      {label(270, 82, "Chloro", "#3B6D11", 7)}
      {/* Labels */}
      {label(310, 215, "Cell wall", C.primaryMid, 8, "middle", "500")}
      {label(310, 225, "(rigid, outer)", C.textMute, 7, "middle")}

      {/* Key differences at bottom */}
      {label(200, 238, "Plant: cell wall + chloroplast + large vacuole | Animal: no cell wall", C.textMute, 9)}
    </svg>
  );
}

// ═══════════════════════════════════════
//  3D SHAPES (Class 9/10 Maths)
// ═══════════════════════════════════════

export function SvgConeSphere() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Cone and sphere — key formulas", C.text, 13, "middle", "600")}
      {/* Cone */}
      <ellipse cx="110" cy="150" rx="50" ry="15" fill="none" stroke={C.primaryMid} strokeWidth="1.5"/>
      <line x1="60" y1="150" x2="110" y2="50" stroke={C.primaryMid} strokeWidth="2"/>
      <line x1="160" y1="150" x2="110" y2="50" stroke={C.primaryMid} strokeWidth="2"/>
      <line x1="110" y1="150" x2="110" y2="50" stroke={C.textMute} strokeWidth="1" strokeDasharray="4,3"/>
      {label(100, 105, "h", C.textMute, 10, "end")}
      {label(110, 163, "r", C.primaryMid, 10)}
      <line x1="110" y1="50" x2="160" y2="150" stroke={C.amberDark} strokeWidth="1" strokeDasharray="4,3"/>
      {label(145, 95, "l", C.amberDark, 10)}
      {label(110, 180, "V = ⅓πr²h", C.primaryMid, 10, "middle", "500")}
      {label(110, 193, "CSA = πrl", C.amberDark, 10, "middle", "500")}

      {/* Sphere */}
      <circle cx="300" cy="105" r="55" fill="none" stroke={C.purpleMid} strokeWidth="2"/>
      <ellipse cx="300" cy="105" rx="55" ry="15" fill="none" stroke={C.purpleMid} strokeWidth="1" strokeDasharray="4,3"/>
      <line x1="300" y1="105" x2="355" y2="105" stroke={C.purpleMid} strokeWidth="1.5"/>
      <circle cx="300" cy="105" r="3" fill={C.purpleMid}/>
      {label(330, 98, "r", C.purpleMid, 10)}
      {label(300, 180, "V = ⅔πr³", C.purpleMid, 10, "middle", "500")}
      {label(300, 193, "SA = 4πr²", C.purpleMid, 10, "middle", "500")}
    </svg>
  );
}

// ═══════════════════════════════════════
//  EXPORT MAP
// ═══════════════════════════════════════

export const SCIENCE_SVG_MAP = {
  "sound-wave": SvgSoundWave,
  "sonar-echo": SvgSonarEcho,
  "magnetic-field-lines": SvgMagneticFieldLines,
  "solenoid": SvgSolenoid,
  "electric-motor": SvgElectricMotor,
  "em-induction": SvgEMInduction,
  "bohr-model": SvgBohrModel,
  "cell-structure": SvgCellStructure,
  "cone-sphere": SvgConeSphere,
};
